/**
 * Utility Functions for CallerID Reputation Monitor
 *
 * This module exports all utility functions and provides
 * common helper functions used throughout the application.
 */

import { Logger, LogCategory } from './logger';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

// ============================================================================
// Re-exports
// ============================================================================

export * from './logger';
export * from './errorHandler';

// ============================================================================
// Class Name Utility (Tailwind CSS)
// ============================================================================

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

// ============================================================================
// Phone Number Formatting
// ============================================================================

export function formatPhoneNumber(phone: string): string {
  Logger.debug(LogCategory.DATA, `Formatting phone number: ${phone.substring(0, 4)}...`, 'formatPhoneNumber');

  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '');

  // Handle different formats
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }

  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }

  // Return original if can't format
  Logger.warn(LogCategory.DATA, `Unable to format phone number, returning original`, 'formatPhoneNumber', {
    originalLength: cleaned.length,
  });
  return phone;
}

export function parsePhoneNumber(phone: string): string {
  // Strip to just digits, keeping leading country code
  return phone.replace(/\D/g, '');
}

export function validatePhoneNumber(phone: string): boolean {
  const cleaned = phone.replace(/\D/g, '');
  const digits = cleaned.length === 11 && cleaned.startsWith('1') ? cleaned.slice(1) : cleaned;

  // Must be exactly 10 digits
  if (digits.length !== 10) return false;

  // Area code can't start with 0 or 1
  if (digits[0] === '0' || digits[0] === '1') return false;

  // Reject all-same-digit numbers (e.g., 9999999999)
  if (/^(\d)\1{9}$/.test(digits)) return false;

  // Reject 555-01xx test numbers
  if (digits.slice(3, 6) === '555' && digits.slice(6, 8) === '01') return false;

  Logger.debug(LogCategory.VALIDATION, `Phone validation: true`, 'validatePhoneNumber', {
    cleanedLength: digits.length,
  });

  return true;
}

// ============================================================================
// Date Formatting
// ============================================================================

export function formatDate(date: Date | string, format: 'short' | 'long' | 'relative' | 'iso' = 'short'): string {
  Logger.debug(LogCategory.DATA, `Formatting date with format: ${format}`, 'formatDate');

  try {
    const d = typeof date === 'string' ? new Date(date) : date;

    if (isNaN(d.getTime())) {
      Logger.warn(LogCategory.DATA, 'Invalid date provided to formatDate', 'formatDate', { date: String(date) });
      return 'Invalid Date';
    }

    switch (format) {
      case 'short':
        return d.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });
      case 'long':
        return d.toLocaleDateString('en-US', {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        });
      case 'relative':
        return formatRelativeTime(d);
      case 'iso':
        return d.toISOString();
      default:
        return d.toLocaleDateString();
    }
  } catch (error) {
    Logger.error(LogCategory.DATA, 'Error formatting date', 'formatDate', { date: String(date) }, error);
    return 'Invalid Date';
  }
}

export function formatDateTime(date: Date | string): string {
  try {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch (error) {
    Logger.error(LogCategory.DATA, 'Error formatting datetime', 'formatDateTime', { date: String(date) }, error);
    return 'Invalid Date';
  }
}

export function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);

  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  if (diffWeeks < 4) return `${diffWeeks} week${diffWeeks === 1 ? '' : 's'} ago`;
  if (diffMonths < 12) return `${diffMonths} month${diffMonths === 1 ? '' : 's'} ago`;
  return formatDate(date, 'short');
}

// ============================================================================
// Number Formatting
// ============================================================================

export function formatNumber(num: number, options?: Intl.NumberFormatOptions): string {
  try {
    return new Intl.NumberFormat('en-US', options).format(num);
  } catch (error) {
    Logger.error(LogCategory.DATA, 'Error formatting number', 'formatNumber', { num }, error);
    return String(num);
  }
}

export function formatCurrency(amount: number, currency: string = 'USD'): string {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  } catch (error) {
    Logger.error(LogCategory.DATA, 'Error formatting currency', 'formatCurrency', { amount, currency }, error);
    return `$${amount.toFixed(2)}`;
  }
}

export function formatPercentage(value: number, decimals: number = 1): string {
  return `${(value * 100).toFixed(decimals)}%`;
}

export function formatCompactNumber(num: number): string {
  if (num < 1000) return String(num);
  if (num < 1000000) return `${(num / 1000).toFixed(1)}K`;
  if (num < 1000000000) return `${(num / 1000000).toFixed(1)}M`;
  return `${(num / 1000000000).toFixed(1)}B`;
}

// ============================================================================
// String Utilities
// ============================================================================

export function truncate(str: string, maxLength: number, suffix: string = '...'): string {
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength - suffix.length) + suffix;
}

export function capitalize(str: string): string {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

export function titleCase(str: string): string {
  return str
    .split(' ')
    .map(word => capitalize(word))
    .join(' ');
}

export function slugify(str: string): string {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '');
}

export function generateId(prefix: string = 'id'): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

// ============================================================================
// Array Utilities
// ============================================================================

export function chunk<T>(array: T[], size: number): T[][] {
  Logger.debug(LogCategory.DATA, `Chunking array of ${array.length} items into chunks of ${size}`, 'chunk');
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
}

export function groupBy<T, K extends string | number>(array: T[], keyFn: (item: T) => K): Record<K, T[]> {
  return array.reduce((acc, item) => {
    const key = keyFn(item);
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {} as Record<K, T[]>);
}

export function unique<T>(array: T[], keyFn?: (item: T) => unknown): T[] {
  if (!keyFn) return [...new Set(array)];

  const seen = new Set();
  return array.filter(item => {
    const key = keyFn(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function sortBy<T>(array: T[], keyFn: (item: T) => string | number, order: 'asc' | 'desc' = 'asc'): T[] {
  const sorted = [...array].sort((a, b) => {
    const aVal = keyFn(a);
    const bVal = keyFn(b);
    if (aVal < bVal) return -1;
    if (aVal > bVal) return 1;
    return 0;
  });
  return order === 'desc' ? sorted.reverse() : sorted;
}

// ============================================================================
// Object Utilities
// ============================================================================

export function pick<T extends object, K extends keyof T>(obj: T, keys: K[]): Pick<T, K> {
  const result = {} as Pick<T, K>;
  keys.forEach(key => {
    if (key in obj) {
      result[key] = obj[key];
    }
  });
  return result;
}

export function omit<T extends object, K extends keyof T>(obj: T, keys: K[]): Omit<T, K> {
  const result = { ...obj };
  keys.forEach(key => {
    delete result[key];
  });
  return result as Omit<T, K>;
}

export function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}

// ============================================================================
// Debounce and Throttle
// ============================================================================

export function debounce<T extends (...args: Parameters<T>) => void>(
  fn: T,
  delayMs: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      fn(...args);
      timeoutId = null;
    }, delayMs);
  };
}

export function throttle<T extends (...args: Parameters<T>) => void>(
  fn: T,
  limitMs: number
): (...args: Parameters<T>) => void {
  let inThrottle = false;

  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      fn(...args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limitMs);
    }
  };
}

// ============================================================================
// Local Storage Helpers
// ============================================================================

export function getFromStorage<T>(key: string, defaultValue: T): T {
  Logger.debug(LogCategory.DATA, `Reading from localStorage: ${key}`, 'getFromStorage');

  try {
    const item = localStorage.getItem(key);
    if (item === null) {
      Logger.debug(LogCategory.DATA, `Key not found in localStorage, using default`, 'getFromStorage', { key });
      return defaultValue;
    }
    const parsed = JSON.parse(item);
    Logger.debug(LogCategory.DATA, `Successfully read from localStorage`, 'getFromStorage', { key });
    return parsed;
  } catch (error) {
    Logger.error(LogCategory.DATA, `Error reading from localStorage`, 'getFromStorage', { key }, error);
    return defaultValue;
  }
}

export function setToStorage<T>(key: string, value: T): boolean {
  Logger.debug(LogCategory.DATA, `Writing to localStorage: ${key}`, 'setToStorage');

  try {
    localStorage.setItem(key, JSON.stringify(value));
    Logger.debug(LogCategory.DATA, `Successfully wrote to localStorage`, 'setToStorage', { key });
    return true;
  } catch (error) {
    Logger.error(LogCategory.DATA, `Error writing to localStorage`, 'setToStorage', { key }, error);
    return false;
  }
}

export function removeFromStorage(key: string): boolean {
  Logger.debug(LogCategory.DATA, `Removing from localStorage: ${key}`, 'removeFromStorage');

  try {
    localStorage.removeItem(key);
    Logger.debug(LogCategory.DATA, `Successfully removed from localStorage`, 'removeFromStorage', { key });
    return true;
  } catch (error) {
    Logger.error(LogCategory.DATA, `Error removing from localStorage`, 'removeFromStorage', { key }, error);
    return false;
  }
}

// ============================================================================
// Clipboard
// ============================================================================

export async function copyToClipboard(text: string): Promise<boolean> {
  Logger.info(LogCategory.UI, 'Copying text to clipboard', 'copyToClipboard', {
    textLength: text.length,
  });

  try {
    await navigator.clipboard.writeText(text);
    Logger.info(LogCategory.UI, 'Successfully copied to clipboard', 'copyToClipboard');
    return true;
  } catch (error) {
    Logger.error(LogCategory.UI, 'Failed to copy to clipboard', 'copyToClipboard', undefined, error);
    return false;
  }
}

// ============================================================================
// URL Utilities
// ============================================================================

export function buildQueryString(params: Record<string, string | number | boolean | undefined | null>): string {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
}

export function parseQueryString(search: string): Record<string, string> {
  const params = new URLSearchParams(search);
  const result: Record<string, string> = {};

  params.forEach((value, key) => {
    result[key] = value;
  });

  return result;
}

// ============================================================================
// Validation Helpers
// ============================================================================

export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValid = emailRegex.test(email);

  Logger.debug(LogCategory.VALIDATION, `Email validation: ${isValid}`, 'isValidEmail', {
    emailDomain: email.split('@')[1],
  });

  return isValid;
}

export function isValidPassword(password: string): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (password.length < 8) {
    errors.push('Password must be at least 8 characters long');
  }
  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }
  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }
  if (!/[0-9]/.test(password)) {
    errors.push('Password must contain at least one number');
  }

  Logger.debug(LogCategory.VALIDATION, `Password validation: ${errors.length === 0}`, 'isValidPassword', {
    errorCount: errors.length,
  });

  return { valid: errors.length === 0, errors };
}

// ============================================================================
// Sleep Utility
// ============================================================================

export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// ============================================================================
// Safe JSON Parse
// ============================================================================

export function safeJsonParse<T>(json: string, defaultValue: T): T {
  try {
    return JSON.parse(json);
  } catch (error) {
    Logger.warn(LogCategory.DATA, 'Failed to parse JSON', 'safeJsonParse', { jsonLength: json.length }, error);
    return defaultValue;
  }
}

// ============================================================================
// Environment Helpers
// ============================================================================

export function isDevelopment(): boolean {
  return import.meta.env.DEV;
}

export function isProduction(): boolean {
  return import.meta.env.PROD;
}

export function getEnvVar(key: string, defaultValue: string = ''): string {
  const value = import.meta.env[key];
  if (value === undefined) {
    Logger.debug(LogCategory.SYSTEM, `Environment variable not found: ${key}`, 'getEnvVar');
    return defaultValue;
  }
  return value;
}
